import * as PIXI from "pixi.js"
import { type ID, type Idea } from "../model/types"
import { defaultIdeaColourResolver, type IdeaColourResolver } from "./idea_colour"
import { clearIdeaTextFromGraphic, renderFullTitleToGraphic } from "./idea_text_rendering"
import { type PixiLayers } from "./pixi_scene"

const MAX_TITLE_TEXTURE_RESOLUTION = 8

export default class FullTitleRenderer {
  private app: PIXI.Application
  private layers: PixiLayers
  private idea_colour_resolver: () => IdeaColourResolver
  private text_by_idea_id: Map<ID, PIXI.Container>
  private signature_by_idea_id: Map<ID, string>

  constructor(
    app: PIXI.Application,
    layers: PixiLayers,
    idea_colour_resolver: () => IdeaColourResolver = () => defaultIdeaColourResolver,
  ) {
    this.app = app
    this.layers = layers
    this.idea_colour_resolver = idea_colour_resolver
    this.text_by_idea_id = new Map()
    this.signature_by_idea_id = new Map()
  }

  render_titles(ideas: Idea[]): void {
    const layer = this.ensure_layer()
    const visible_idea_ids = new Set(ideas.map((idea) => idea.id))

    Array.from(this.text_by_idea_id.keys()).forEach((id) => {
      if (!visible_idea_ids.has(id)) this.clear_title(id)
    })

    ideas.forEach((idea) => {
      const signature = this.signature(idea)
      if (this.signature_by_idea_id.get(idea.id) === signature) return

      this.clear_title(idea.id)
      renderFullTitleToGraphic(
        layer,
        idea,
        this.text_by_idea_id,
        0,
        0,
        this.idea_colour_resolver(),
        MAX_TITLE_TEXTURE_RESOLUTION,
      )
      this.signature_by_idea_id.set(idea.id, signature)
    })

    layer.renderable = ideas.length > 0
  }

  clear_titles(): void {
    Array.from(this.text_by_idea_id.keys()).forEach((id) => this.clear_title(id))
    if (this.layers.full_titles) this.layers.full_titles.renderable = false
  }

  hide_titles(idea_ids: ID[]): void {
    idea_ids.forEach((id) => this.clear_title(id))
    if (this.layers.full_titles && this.text_by_idea_id.size === 0)
      this.layers.full_titles.renderable = false
  }

  private ensure_layer(): PIXI.Container {
    if (!this.layers.full_titles) {
      this.layers.full_titles = new PIXI.Container()
      this.layers.full_titles.renderable = false
    }

    const layer = this.layers.full_titles
    const hover_index = this.app.stage.children.indexOf(this.layers.hover)
    const layer_index = this.app.stage.children.indexOf(layer)

    if (layer_index === -1) {
      this.app.stage.addChildAt(layer, hover_index)
    } else if (layer_index !== hover_index - 1) {
      this.app.stage.setChildIndex(layer, Math.max(0, hover_index - 1))
    }

    return layer
  }

  private clear_title(id: ID): void {
    const layer = this.layers.full_titles
    const text = this.text_by_idea_id.get(id)
    if (layer && text) clearIdeaTextFromGraphic(layer, { id } as Idea, this.text_by_idea_id)
    this.signature_by_idea_id.delete(id)
  }

  private signature(idea: Idea): string {
    return [idea.title, idea.memo, idea.type, this.idea_colour_resolver()(idea)].join("\n")
  }
}
